import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

/**
 * Trigger-maintained rollup columns and the virtual filter ids that read them.
 *
 * Same shape as the existing deal rollups (`has_active_deals` /
 * `active_deal_count` in `company-fields-basic.ts`): the value is a real
 * column on `public.companies`, kept current by a DB trigger, so it is
 * filterable / sortable / groupable through the generic CRM table rather than
 * needing a bespoke query per question.
 *
 * See `supabase/migrations/20260818160000_crm_derived_rollups.sql`.
 */

/** Views that show the contract-coverage columns. */
const CONTRACT_VIEWS = [
  "all-companies",
  "status-client",
  "under-contract",
  "no-contract",
  "contract-expiring-soon",
] as const;

export const companyDerivedFields = defineFieldConfigMap({
  // FIELD: has_signed_contract
  has_signed_contract: {
    type: "boolean",
    label: "Under Contract",
    mobileDetailSectionId: "commercial",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Maintained automatically from signed contracts on this company. Paper the contract to change it.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "contracts",
      sourceField: "status",
      sourceForeignKeyField: "company_id",
      notes: "True when at least one related contract has status 'signed'.",
    },
    filterType: "exact",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "FileSignature",
    groupable: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "has_signed_contract",
    savedViewMembership: CONTRACT_VIEWS,
  },
  // FIELD: contract_expires_at
  contract_expires_at: {
    type: "date",
    label: "Contract Expires",
    mobileDetailSectionId: "commercial",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Latest expiry across this company's signed contracts. Edit the contract's expiry, not the company.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "contracts",
      sourceField: "expires_at",
      sourceForeignKeyField: "company_id",
      notes:
        "MAX(expires_at) over signed contracts — the date paper coverage runs out. Past = lapsed, future = covered.",
    },
    filterType: "dateRange",
    width: 150,
    minWidth: 120,
    maxWidth: 190,
    icon: "CalendarClock",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: ["thismonth", "nextmonth", "thisquarter", "nextquarter"],
      showCustomRange: true,
    },
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "contract_expires_at",
    savedViewMembership: CONTRACT_VIEWS,
  },
  // FIELD: has_open_tickets
  has_open_tickets: {
    type: "boolean",
    label: "Has Open Tickets",
    mobileDetailSectionId: "commercial",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason: "Maintained automatically from this company's non-terminal tickets.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "tickets",
      sourceField: "status",
      sourceForeignKeyField: "company_id",
      notes:
        "True when at least one ticket is not resolved / closed / cancelled. Unlike the month-scoped ticket saved views, this is live state.",
    },
    filterType: "exact",
    width: 150,
    minWidth: 120,
    maxWidth: 180,
    icon: "LifeBuoy",
    groupable: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "has_open_tickets",
    savedViewMembership: ["all-companies", "with-open-tickets"],
  },
  // FIELD: open_ticket_count
  open_ticket_count: {
    type: "number",
    label: "Open Tickets",
    mobileDetailSectionId: "commercial",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason: "Maintained automatically from this company's non-terminal tickets.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "tickets",
      sourceField: "id",
      sourceForeignKeyField: "company_id",
      notes: "Count of tickets not in a terminal status (resolved / closed / cancelled).",
    },
    filterType: "range",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "LifeBuoy",
    groupable: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "open_ticket_count",
    savedViewMembership: ["all-companies", "with-open-tickets"],
  },
  // FIELD: active_contact_count
  active_contact_count: {
    type: "number",
    label: "Contacts",
    mobileDetailSectionId: "basic",
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    isReadOnlyDerived: true,
    readOnlyReason: "Maintained automatically from the active contacts attached to this company.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "contacts",
      sourceField: "is_active",
      sourceForeignKeyField: "company_id",
      notes:
        "Counts ACTIVE contacts only, so a company whose only people are deactivated reads as orphaned (count 0).",
    },
    filterType: "range",
    width: 110,
    minWidth: 90,
    maxWidth: 140,
    icon: "Users",
    groupable: true,
    allowedRoles: [...ALL_STAFF_ROLES],
    showOnCard: true,
    cardSpan: 1,
    graphqlSelector: "active_contact_count",
    savedViewMembership: ["all-companies"],
  },
  /**
   * FILTER: contract_expiring_within_days
   * Virtual filter id (not a column). Saved views pass
   * `filter_contract_expiring_within_days`; filter-conversion strips the
   * `filter_` prefix and routes here via `toGraphQLFilter`.
   *
   * Expressed as "days from now" rather than a stored flag so the renewal
   * window stays correct without a nightly job rewriting rows.
   */
  contract_expiring_within_days: {
    type: "number",
    label: "Contract expiring within (days)",
    filterable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    formOnly: true,
    toGraphQLFilter: (value, context) => {
      const days = typeof value === "number" ? value : Number(value);
      // Returning an EMPTY object (not null) is deliberate: null falls through
      // to the generic converter, which would emit `{ <virtual id>: { eq: … } }`
      // for a field that is not a column and make PostgREST reject the whole
      // query. An empty object is truthy, so the generic path is skipped and
      // the filter contributes nothing.
      if (!Number.isFinite(days) || days < 0) return {};
      const cutoff = new Date(context.now);
      cutoff.setUTCDate(cutoff.getUTCDate() + days);
      return {
        contract_expires_at: {
          gte: toDateOnly(context.now),
          lte: toDateOnly(cutoff),
        },
      };
    },
  },
  /**
   * FILTER: contract_lapsed
   * Virtual filter id. True-valued filter meaning "had paper, coverage has run
   * out" — distinct from "never had a contract", which is
   * `has_signed_contract: false`.
   */
  contract_lapsed: {
    type: "boolean",
    label: "Contract lapsed",
    filterable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    formOnly: true,
    toGraphQLFilter: (value, context) => {
      // Returning an EMPTY object (not null) is deliberate: null falls through
      // to the generic converter, which would emit `{ <virtual id>: { eq: … } }`
      // for a field that is not a column and make PostgREST reject the whole
      // query. An empty object is truthy, so the generic path is skipped and
      // the filter contributes nothing.
      if (value !== true) return {};
      return {
        has_signed_contract: { eq: true },
        contract_expires_at: { lt: toDateOnly(context.now) },
      };
    },
  },
  /**
   * FILTER: not_contacted_within_days
   * Virtual filter id. The inverse of the `last_contacted_at` datetime presets
   * (which all express "contacted within X"): this asks for accounts that have
   * gone quiet, including those never contacted at all.
   */
  not_contacted_within_days: {
    type: "number",
    label: "Not contacted in (days)",
    filterable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    formOnly: true,
    toGraphQLFilter: (value, context) => {
      const days = typeof value === "number" ? value : Number(value);
      // Returning an EMPTY object (not null) is deliberate: null falls through
      // to the generic converter, which would emit `{ <virtual id>: { eq: … } }`
      // for a field that is not a column and make PostgREST reject the whole
      // query. An empty object is truthy, so the generic path is skipped and
      // the filter contributes nothing.
      if (!Number.isFinite(days) || days < 0) return {};
      const cutoff = new Date(context.now);
      cutoff.setUTCDate(cutoff.getUTCDate() - days);
      return {
        or: [
          { last_contacted_at: { is: "NULL" } },
          { last_contacted_at: { lt: cutoff.toISOString() } },
        ],
      };
    },
  },
});

/**
 * `contract_expires_at` is a DATE column — comparing it against a full
 * timestamp string makes PostgREST coerce on every row, so send `YYYY-MM-DD`.
 */
function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}
